import { Directive, ElementRef, Input, NgZone, OnDestroy } from '@angular/core';

type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

/**
 * Lightweight replacement for PrimeNG's `pTooltip` on high-frequency table cells.
 * Renders the same `.p-tooltip` markup (so theming is identical) but builds the
 * overlay only on hover, with listeners bound outside Angular's zone. This avoids
 * one PrimeNG BaseComponent instance per cell and its per-cycle change detection.
 */
@Directive({
    selector: '[appLazyTooltip]',
    standalone: true,
})
export class LazyTooltipDirective implements OnDestroy {
    @Input('appLazyTooltip') text: string | number | null | undefined;
    @Input() tooltipPosition: TooltipPosition = 'bottom';
    @Input() lazyTooltipStyleClass?: string;

    private container: HTMLElement | null = null;

    private readonly onEnter = (): void => this.show();
    private readonly onLeave = (): void => this.hide();

    constructor(
        private readonly host: ElementRef<HTMLElement>,
        private readonly zone: NgZone,
    ) {
        this.zone.runOutsideAngular(() => {
            const el = this.host.nativeElement;
            el.addEventListener('mouseenter', this.onEnter);
            el.addEventListener('mouseleave', this.onLeave);
        });
    }

    ngOnDestroy(): void {
        const el = this.host.nativeElement;
        el.removeEventListener('mouseenter', this.onEnter);
        el.removeEventListener('mouseleave', this.onLeave);
        this.hide();
    }

    private show(): void {
        const label = this.text == null ? '' : String(this.text);
        if (!label.trim()) return;
        this.hide();

        const container = document.createElement('div');
        container.className = `p-tooltip p-component p-tooltip-${this.tooltipPosition}`
            + (this.lazyTooltipStyleClass ? ` ${this.lazyTooltipStyleClass}` : '');
        container.setAttribute('role', 'tooltip');
        container.setAttribute('data-pc-section', 'root');
        container.style.position = 'absolute';
        // Theme base CSS sets `.p-tooltip { display: none }`; override it to make the tooltip visible.
        container.style.display = 'inline-block';
        container.style.pointerEvents = 'none';
        container.style.zIndex = '1100';
        container.style.visibility = 'hidden';

        const arrow = document.createElement('div');
        arrow.className = 'p-tooltip-arrow';
        arrow.setAttribute('data-pc-section', 'arrow');

        const textEl = document.createElement('div');
        textEl.className = 'p-tooltip-text';
        textEl.setAttribute('data-pc-section', 'text');
        textEl.appendChild(document.createTextNode(label));

        container.appendChild(arrow);
        container.appendChild(textEl);
        document.body.appendChild(container);

        this.container = container;
        this.align(arrow);
        container.style.visibility = '';
    }

    private hide(): void {
        this.container?.remove();
        this.container = null;
    }

    private align(arrow: HTMLElement): void {
        if (!this.container) return;
        const host = this.host.nativeElement.getBoundingClientRect();
        const scrollLeft = window.scrollX;
        const scrollTop = window.scrollY;
        const width = this.container.offsetWidth;
        const height = this.container.offsetHeight;

        let left = 0;
        let top = 0;
        arrow.style.top = '';
        arrow.style.right = '';
        arrow.style.bottom = '';
        arrow.style.left = '';

        switch (this.tooltipPosition) {
            case 'top':
                left = host.left + scrollLeft + (host.width - width) / 2;
                top = host.top + scrollTop - height;
                arrow.style.bottom = '0';
                arrow.style.left = `${width / 2}px`;
                break;
            case 'left':
                left = host.left + scrollLeft - width;
                top = host.top + scrollTop + (host.height - height) / 2;
                arrow.style.top = '50%';
                arrow.style.right = '0';
                break;
            case 'right':
                left = host.left + scrollLeft + host.width;
                top = host.top + scrollTop + (host.height - height) / 2;
                arrow.style.top = '50%';
                arrow.style.left = '0';
                break;
            case 'bottom':
            default:
                left = host.left + scrollLeft + (host.width - width) / 2;
                top = host.top + scrollTop + host.height;
                arrow.style.top = '0';
                arrow.style.left = `${width / 2}px`;
                break;
        }

        this.container.style.left = `${Math.round(left)}px`;
        this.container.style.top = `${Math.round(top)}px`;
    }
}
